import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { BroadcastMessage } from '../../../types/notification-admin';
import { NotificationBroadcastService } from '../../../services/notificationBroadcastService';
import { NotificationPriority } from '../../../types/user';

interface BroadcastHistoryProps {
  onViewBroadcast?: (broadcast: BroadcastMessage) => void;
  refreshTrigger?: number;
}

/**
 * BroadcastHistory - Component showing past and scheduled broadcasts
 */
const BroadcastHistory: React.FC<BroadcastHistoryProps> = ({
  onViewBroadcast,
  refreshTrigger = 0,
}) => {
  const [broadcasts, setBroadcasts] = useState<BroadcastMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBroadcast, setSelectedBroadcast] = useState<BroadcastMessage | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    loadBroadcasts();
  }, [refreshTrigger]);

  const loadBroadcasts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await NotificationBroadcastService.getBroadcastHistory(50);
      setBroadcasts(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load broadcast history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (broadcast: BroadcastMessage) => {
    setSelectedBroadcast(broadcast);
    setDetailDialogOpen(true);
  };

  const handleCancelBroadcast = async (broadcastId: string) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled broadcast?')) {
      return;
    }

    try {
      await NotificationBroadcastService.cancelBroadcast(broadcastId);
      await loadBroadcasts();
    } catch (err: any) {
      alert(`Failed to cancel broadcast: ${err.message}`);
    }
  };

  const handleDeleteBroadcast = async (broadcastId: string) => {
    if (!window.confirm('Are you sure you want to delete this broadcast?')) {
      return;
    }

    try {
      await NotificationBroadcastService.deleteBroadcast(broadcastId);
      await loadBroadcasts();
    } catch (err: any) {
      alert(`Failed to delete broadcast: ${err.message}`);
    }
  };

  const getStatusColor = (status: BroadcastMessage['status']) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'scheduled':
        return 'info';
      case 'sending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: NotificationPriority) => {
    switch (priority) {
      case NotificationPriority.CRITICAL:
        return 'error';
      case NotificationPriority.HIGH:
        return 'warning';
      case NotificationPriority.NORMAL:
        return 'info';
      case NotificationPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (broadcasts.length === 0) {
    return (
      <Alert severity="info">
        No broadcasts found. Create your first broadcast to send notifications to multiple users.
      </Alert>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Recipients</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Sent/Scheduled</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {broadcasts.map((broadcast) => (
              <TableRow key={broadcast.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {broadcast.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {broadcast.message.substring(0, 50)}
                    {broadcast.message.length > 50 ? '...' : ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{broadcast.recipientCount}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={broadcast.priority}
                    color={getPriorityColor(broadcast.priority) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={broadcast.status}
                    color={getStatusColor(broadcast.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {format(broadcast.createdAt, 'PP p')}
                  </Typography>
                </TableCell>
                <TableCell>
                  {broadcast.sentAt ? (
                    <Typography variant="caption">
                      {format(broadcast.sentAt, 'PP p')}
                    </Typography>
                  ) : broadcast.scheduledFor ? (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="caption">
                        {format(broadcast.scheduledFor, 'PP p')}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleViewDetails(broadcast)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {broadcast.status === 'scheduled' && (
                    <Tooltip title="Cancel Broadcast">
                      <IconButton
                        size="small"
                        onClick={() => handleCancelBroadcast(broadcast.id)}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {['draft', 'cancelled', 'failed'].includes(broadcast.status) && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteBroadcast(broadcast.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedBroadcast && (
          <>
            <DialogTitle>Broadcast Details</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Title
                </Typography>
                <Typography variant="body1" paragraph>
                  {selectedBroadcast.title}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary">
                  Message
                </Typography>
                <Typography variant="body1" paragraph>
                  {selectedBroadcast.message}
                </Typography>

                <Box display="flex" gap={2} mb={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Priority
                    </Typography>
                    <Chip
                      label={selectedBroadcast.priority}
                      color={getPriorityColor(selectedBroadcast.priority) as any}
                      size="small"
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={selectedBroadcast.status}
                      color={getStatusColor(selectedBroadcast.status) as any}
                      size="small"
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Recipients
                    </Typography>
                    <Typography variant="body1">{selectedBroadcast.recipientCount}</Typography>
                  </Box>
                </Box>

                {selectedBroadcast.actionLabel && selectedBroadcast.actionUrl && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">
                      Action Button
                    </Typography>
                    <Typography variant="body2">
                      {selectedBroadcast.actionLabel} → {selectedBroadcast.actionUrl}
                    </Typography>
                  </>
                )}

                {selectedBroadcast.errorMessage && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {selectedBroadcast.errorMessage}
                  </Alert>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default BroadcastHistory;
